import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/hen-ui/utils';

/** Thickness keys shared by the variant map and Storybook controls. */
export const SEPARATOR_THICKNESSES = ['1', '2', '4', '8'] as const;

export type SeparatorThickness = (typeof SEPARATOR_THICKNESSES)[number];

/** Corner radius keys shared by the variant map and Storybook controls. */
export const SEPARATOR_RADII = [
  'xxs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  'max',
] as const;

export type SeparatorRadius = (typeof SEPARATOR_RADII)[number];

/** Paint choices shared by the component and the catalog select. */
export const SEPARATOR_FILLS = ['solid', 'rule'] as const;

export type SeparatorFill = (typeof SEPARATOR_FILLS)[number];

/** Image painted over the solid divider when an author opts into the rule. */
export const separatorRuleFillClass = '[background-image:var(--border-rule)]';

function separatorRuleFillClassName(
  fill?: SeparatorFill | '' | null,
): string | undefined {
  return fill === 'rule' ? separatorRuleFillClass : undefined;
}

/** Tone keys shared by the background helper and Storybook controls. */
export const SEPARATOR_TONES = ['default', 'stronger', 'inverse'] as const;

export type SeparatorTone = (typeof SEPARATOR_TONES)[number];

const separatorToneClass = {
  default: 'bg-border-divider',
  stronger: 'bg-border-divider-strong',
  inverse: 'bg-border-divider-inverse',
} as const satisfies Record<SeparatorTone, string>;

/**
 * Background class for a separator tone.
 * Omitted, empty, and default keep the usual divider.
 */
export function separatorBackgroundClass(
  tone?: SeparatorTone | '' | null,
): string {
  if (tone === 'stronger' || tone === 'inverse') {
    return separatorToneClass[tone];
  }
  return separatorToneClass.default;
}

/** Empty catalog values fall through to the CVA default tone. */
function separatorToneVariant(
  tone?: SeparatorTone | '' | null,
): SeparatorTone | undefined {
  if (tone === 'default' || tone === 'stronger' || tone === 'inverse') {
    return tone;
  }
  return undefined;
}

/**
 * A rule fill keeps the usual divider under the gradient.
 * Stronger and inverse stay on the solid fill.
 */
function separatorPaintTone(
  fill: SeparatorFill | '' | null | undefined,
  tone: SeparatorTone | '' | null | undefined,
): SeparatorTone | undefined {
  if (fill === 'rule') {
    return 'default';
  }
  return separatorToneVariant(tone);
}

type SeparatorOrientation = 'horizontal' | 'vertical';

/**
 * Cross-axis size for each thickness key.
 * Theme border widths fall back to a unitless number, so the arbitrary
 * utility supplies the same variable with a length fallback.
 */
export const separatorThicknessClass = {
  horizontal: {
    '1': 'h-[var(--border-1,1px)]',
    '2': 'h-[var(--border-2,2px)]',
    '4': 'h-[var(--border-4,4px)]',
    '8': 'h-[var(--border-8,8px)]',
  },
  vertical: {
    '1': 'w-[var(--border-1,1px)]',
    '2': 'w-[var(--border-2,2px)]',
    '4': 'w-[var(--border-4,4px)]',
    '8': 'w-[var(--border-8,8px)]',
  },
} as const satisfies Record<
  SeparatorOrientation,
  Record<SeparatorThickness, string>
>;

export const separatorRadiusClass = {
  xxs: 'rounded-xxs',
  xs: 'rounded-xs',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  max: 'rounded-max',
} as const satisfies Record<SeparatorRadius, string>;

export const separatorVariants = cva('shrink-0', {
  variants: {
    orientation: {
      horizontal: 'w-full',
      vertical: 'h-full',
    },
    thickness: {
      '1': '',
      '2': '',
      '4': '',
      '8': '',
    },
    radius: separatorRadiusClass,
    tone: separatorToneClass,
  },
  compoundVariants: [
    {
      orientation: 'horizontal',
      thickness: '1',
      class: separatorThicknessClass.horizontal['1'],
    },
    {
      orientation: 'horizontal',
      thickness: '2',
      class: separatorThicknessClass.horizontal['2'],
    },
    {
      orientation: 'horizontal',
      thickness: '4',
      class: separatorThicknessClass.horizontal['4'],
    },
    {
      orientation: 'horizontal',
      thickness: '8',
      class: separatorThicknessClass.horizontal['8'],
    },
    {
      orientation: 'vertical',
      thickness: '1',
      class: separatorThicknessClass.vertical['1'],
    },
    {
      orientation: 'vertical',
      thickness: '2',
      class: separatorThicknessClass.vertical['2'],
    },
    {
      orientation: 'vertical',
      thickness: '4',
      class: separatorThicknessClass.vertical['4'],
    },
    {
      orientation: 'vertical',
      thickness: '8',
      class: separatorThicknessClass.vertical['8'],
    },
  ],
  defaultVariants: {
    orientation: 'horizontal',
    tone: 'default',
  },
});

export type SeparatorVariantProps = VariantProps<typeof separatorVariants>;

const separatorLabeledRuleVariants = cva('min-w-0 flex-1', {
  variants: {
    thickness: {
      '1': separatorThicknessClass.horizontal['1'],
      '2': separatorThicknessClass.horizontal['2'],
      '4': separatorThicknessClass.horizontal['4'],
      '8': separatorThicknessClass.horizontal['8'],
    },
    radius: separatorRadiusClass,
    tone: separatorToneClass,
  },
  defaultVariants: {
    tone: 'default',
  },
});

const separatorHairlineClass = {
  horizontal: 'h-px',
  vertical: 'w-px',
} as const satisfies Record<SeparatorOrientation, string>;

export function separatorClassName({
  orientation = 'horizontal',
  thickness,
  radius,
  fill,
  tone,
  className,
}: {
  orientation?: SeparatorOrientation | null;
  thickness?: SeparatorThickness | null;
  radius?: SeparatorRadius | null;
  fill?: SeparatorFill | '' | null;
  tone?: SeparatorTone | '' | null;
  className?: string;
}): string {
  const resolvedOrientation =
    orientation === 'vertical' ? 'vertical' : 'horizontal';
  const resolvedThickness = thickness ?? undefined;

  return cn(
    separatorVariants({
      orientation: resolvedOrientation,
      thickness: resolvedThickness,
      radius: radius ?? undefined,
      tone: separatorPaintTone(fill, tone),
    }),
    // Apply the hairline only when thickness is omitted. Keeping h-px / w-px
    // beside an arbitrary length leaves the 1px size in the class list.
    resolvedThickness ? undefined : separatorHairlineClass[resolvedOrientation],
    separatorRuleFillClassName(fill),
    className,
  );
}

export function separatorLabeledRuleClassName({
  thickness,
  radius,
  fill,
  tone,
}: {
  thickness?: SeparatorThickness | null;
  radius?: SeparatorRadius | null;
  fill?: SeparatorFill | '' | null;
  tone?: SeparatorTone | '' | null;
}): string {
  const resolvedThickness = thickness ?? undefined;

  return cn(
    separatorLabeledRuleVariants({
      thickness: resolvedThickness,
      radius: radius ?? undefined,
      tone: separatorPaintTone(fill, tone),
    }),
    resolvedThickness ? undefined : separatorHairlineClass.horizontal,
    separatorRuleFillClassName(fill),
  );
}
